<script lang="ts" setup>
import bus from '@/helper/Bus'
import { BusEvent } from '@/helper/Types'
import { onMounted, onUnmounted, ref } from 'vue'

const props = defineProps<{
  sourceImg: HTMLImageElement
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const root = ref<HTMLDivElement | null>(null)
const wrapper = ref<HTMLDivElement | null>(null)
const imgElement = ref<HTMLImageElement | null>(null)
const img = ref<HTMLDivElement | null>(null)

const imgUrl = ref<string>('')
const innerPadding = ref<number>(38)
const imgPaddingTop = ref<number>(16)

const animTime = 500
const COMPONENT_NAME = 'lightbox'

function close(): void {
  root.value?.classList.remove('is--zoomed')
  setInitialPos()
  setTimeout(() => {
    emit('close')
    document.body.style.overflow = ''
  }, animTime)
}

bus.$onC(COMPONENT_NAME, BusEvent.KEYPRESSED_ESC, close)

onMounted((): void => {
  imgUrl.value = props.sourceImg.src
  document.body.style.overflow = 'hidden'
  window.addEventListener('resize', windowResizeHandler)
  setInitialPos()
  setTimeout(() => {
    root.value?.classList.add('is--zoomed')
    setImgRatio()
  }, 0) // timeout to make sure that is--zoomed is added after inital pos set
})

onUnmounted((): void => {
  window.removeEventListener('resize', windowResizeHandler)
  bus.$offC(COMPONENT_NAME)
})

/**
 * Recalculates the aspect ratios on window resize
 */
function windowResizeHandler() {
  setImgRatio()
}

/**
 * Sets the orientation class for the image container depending on the aspect ratio of the viewport
 * in comparison to the aspect ratio of the image
 */
function setImgRatio(): void {
  if (!root.value) {
    return
  }

  const imgRatio = props.sourceImg.naturalWidth / props.sourceImg.naturalHeight
  const innerLightBoxWidth = root.value.clientWidth - innerPadding.value * 2
  const innerLightBoxHeight = root.value.clientHeight - innerPadding.value * 2
  const portrait =
    (innerLightBoxWidth / props.sourceImg.naturalWidth) * props.sourceImg.naturalHeight + imgPaddingTop.value >
    innerLightBoxHeight

  let wrapperLeft
  let wrapperTop
  let wrapperRight
  let wrapperBottom

  if (portrait) {
    wrapperTop = innerPadding.value
    wrapperBottom = innerPadding.value
    const wrapperWidth = (innerLightBoxHeight - imgPaddingTop.value) * imgRatio
    wrapperLeft = (root.value.clientWidth - wrapperWidth) / 2
    wrapperRight = wrapperLeft
  } else {
    //landscape
    wrapperRight = innerPadding.value
    wrapperLeft = innerPadding.value
    const wrapperHeight = innerLightBoxWidth / imgRatio + imgPaddingTop.value
    wrapperTop = (root.value.clientHeight - wrapperHeight) / 2
    wrapperBottom = wrapperTop
  }
  if (!wrapper.value) {
    return
  }

  wrapper.value.style.top = wrapperTop + 'px'
  wrapper.value.style.left = wrapperLeft + 'px'
  wrapper.value.style.right = wrapperRight + 'px'
  wrapper.value.style.bottom = wrapperBottom + 'px'
}

/**
 * Sets the initial position for the starting and ending of the zoom animation for the lightbox image
 * Creates the effect to zoom in and out from the actual position of the original image
 */
function setInitialPos(): void {
  if (!root.value) {
    return
  }

  const initialPos = props.sourceImg.getBoundingClientRect()
  const parentPos = root.value.getBoundingClientRect()

  if (!wrapper.value) {
    return
  }

  wrapper.value.style.top = initialPos.top + 'px'
  wrapper.value.style.left = initialPos.left + 'px'
  wrapper.value.style.right = parentPos.width - initialPos.left - initialPos.width + 'px'
  wrapper.value.style.bottom = parentPos.height - initialPos.top - initialPos.height + 'px'
}
</script>

<template>
  <div ref="root" class="lightbox" @click.self="close">
    <div ref="wrapper" class="lightbox__wrapper">
      <div ref="img" class="lightbox__image">
        <button class="close-btn lightbox__close-btn" @click.prevent="close">
          <i class="ic-times" />
        </button>

        <div class="lightbox__image-container">
          <img ref="imgElement" :src="imgUrl" alt="">
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$animTime: 0.5s;
$animTimeShort: 0.3s;
$innerPadding: 38;
$imagePaddingTop: 16;

.lightbox {
  display: flex;
  z-index: 9;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color $animTime ease;

  &.is--zoomed {
    background-color: rgba(0, 0, 0, 0.6);
    align-items: center;
    justify-content: center;
  }

  &__close-btn {
    position: absolute;
    color: #fff;
    z-index: 10;
    right: rem-calc(12);
    top: rem-calc(12);
    transition: opacity 0.2s ease;
    opacity: 0;

    .is--zoomed & {
      transition: opacity 0.2s ease $animTimeShort;
      opacity: 1;
    }
  }

  &__image {
    transition: padding-top $animTime cubic-bezier(0.16, 0, 0, 1), opacity 0.1s ease $animTime;
    height: 100%;
    width: 100%;
    position: relative;
    border-radius: rem-calc(10);
    overflow: hidden;
    background: #fff;
    opacity: 0;

    max-width: 100%;

    .is--zoomed & {
      transition: opacity 0.2s ease;
      padding-top: rem-calc(16);
      opacity: 1;
    }
  }

  &__image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: auto;
    }
  }

  &__wrapper {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all $animTime cubic-bezier(0.16, 0, 0, 1);
  }
}
</style>
